import {ChangeDetectorRef, Component, ElementRef, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {switchMap, take} from 'rxjs/operators';
import {FormsModule, NgForm} from '@angular/forms';
import {Router} from '@angular/router';
import {UserProfileService} from '../user-profile.service';
import {UserAddressOptionDto, UserProfileDto, UserRole} from '../../../../models/generated-models';
import {AuthService} from '../../../shared/services/auth.service';
import {ToastService} from '../../../shared/commons/toast/toast-service';
import {maskPhone} from '../user-profile-form';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {
  SubdistrictAutocompleteComponent
} from '../../../shared/commons/subdistrict-autocomplete/subdistrict-autocomplete.component';
import {STAFF_ROLES} from '../../../app.constants';
import {CurrentUserContextService} from '../../../shared/services/current-user-context.service';

@Component({
  selector: 'app-user-profile-onboarding',
  standalone: true,
  imports: [CommonModule, FormsModule, ThaiDateTimePipe, SubdistrictAutocompleteComponent],
  templateUrl: './user-profile-onboarding.html',
  styleUrls: ['./user-profile-onboarding.scss'],
})
export class UserProfileOnboarding implements OnInit {
  // Onboarding no longer asks for a business role: every new user stays USER
  // and only completes their personal info + contact address here.
  public profile: any = {personalInfo: {}, address: {}, userRoles: ['USER']};
  protected currentUserSummary: UserProfileDto = {id: null, username: '-', email: '-', cardNo: '-', fullName: '-'};
  protected profileCompleted = false;
  protected profileSaving = false;
  protected profileError;

  private readonly profileService = inject(UserProfileService);
  private readonly authService = inject(AuthService);
  private readonly currentUserContext = inject(CurrentUserContextService);
  private readonly toast = inject(ToastService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
  }

  ngOnInit(): void {
    this.authService.userRoles$
      .pipe(take(1))
      .subscribe(roles => {
        if (STAFF_ROLES.some(role => roles.includes(role))) {
          this.router.navigate(['/dashboard']);
        }
      });

    this.profileService.getPersonalProfile().subscribe({
      next: (profile) => {
        const {summary, completed} = this.applyProfileToFormModel(profile);
        this.currentUserSummary = summary;
        this.profileCompleted = completed;
        this.cdr.detectChanges();
      },
      error: () => {
      }
    });
  }

  protected get thaiFullName(): string {
    const pi = this.profile.personalInfo ?? {};
    const parts = [
      pi.prefixName,
      pi.firstName,
      pi.middleName,
      pi.lastName,
    ].filter((x: string) => !!x);
    return parts.length > 0 ? parts.join(' ') : '—';
  }

  protected get engFullName(): string {
    const pi = this.profile.personalInfo ?? {};
    const parts = [
      pi.prefixNameEn,
      pi.firstNameEn,
      pi.middleNameEn,
      pi.lastNameEn,
    ].filter((x: string) => !!x);
    return parts.length > 0 ? parts.join(' ') : '—';
  }

  protected getCardTypeLabel(code: string | null | undefined): string {
    const labels: Record<string, string> = {
      ID_CARD: 'เลขประจำตัวประชาชน',
      PASSPORT: 'เลขที่หนังสือเดินทาง',
    };
    return code ? (labels[code] ?? code) : '';
  }

  onPhoneInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const value = maskPhone(input.value);
    input.value = value;
    if (this.profile.personalInfo) {
      this.profile.personalInfo[controlName] = value;
    }
  }

  onSubDistrictSelect(option: UserAddressOptionDto | null): void {
    if (!option) {
      this.profile.address.districtName = '';
      this.profile.address.provinceName = '';
      this.profile.address.postCode = '';
      this.profile.address.districtId = null;
      this.profile.address.provinceId = null;
      this.profile.address.subDistrictId = null;
      this.profile.address.subdistrictName = '';
      this.profile.address.location = '';
      return;
    }
    this.profile.address.subDistrictId = option.subdistrictId ?? null;
    this.profile.address.subdistrictName = option.subdistrictTitle ?? '';
    this.profile.address.districtId = option.districtId ?? null;
    this.profile.address.districtName = option.districtTitle ?? '';
    this.profile.address.provinceId = option.provinceId ?? null;
    this.profile.address.provinceName = option.provinceTitle ?? '';
    this.profile.address.postCode = option.zipcode ?? '';
    this.profile.address.location = [option.subdistrictTitle, option.districtTitle, option.provinceTitle].filter(Boolean).join(' >> ');
  }

  onSubmit(form: NgForm): void {
    // The form's ngSubmit is the single submit entry point. This also protects
    // against rapid repeated submissions before Angular disables the button.
    if (this.profileSaving) {
      return;
    }
    if (form.invalid) {
      this.profileError = 'กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนก่อนบันทึก';
      setTimeout(() => this.scrollToFirstInvalid());
      return;
    }
    this.profileSaving = true;
    this.profileError = null;
    const payload: UserProfileDto = this.buildSavePayload();
    this.profileService.savePersonalProfile(payload)
      .subscribe({
        next: () => {
          // Refresh the session, then the shared user context, and only then
          // navigate: profileCompletionGuard reads the cached context, so
          // navigating early bounces back to onboarding on the first click.
          this.authService.refreshSessionAndProfile().pipe(
            switchMap(() => this.currentUserContext.refresh()),
          ).subscribe({
            next: () => {
              this.toast.success('บันทึกข้อมูลเรียบร้อยแล้ว');
              this.profileSaving = false;
              this.router.navigate(['/profile']);
            },
            error: (err) => {
              this.profileSaving = false;
              this.profileError = err?.error?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง';
              console.error('Refresh profile error:', err);
            },
          });
        },
        error: (err) => {
          this.profileSaving = false;
          this.profileError = err?.error?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง';
          console.error('Save profile error:', err);
        }
      });
  }

  private applyProfileToFormModel(profile: UserProfileDto): { summary: UserProfileDto; completed: boolean } {
    // The onboarding page always carries role USER, even when the stored
    // profile already has another role.
    const completed = profile.profileCompleted ?? true;
    const summary: UserProfileDto = {
      id: profile.id ?? null,
      username: profile.username || '-',
      email: profile.email || '-',
      cardNo: profile.cardNo || '-',
      fullName: profile.fullName || '-'
    };

    this.profile.userRoles = ['USER'];
    this.profile.status = profile.status ?? true;
    this.profile.personalInfo = {
      cardType: profile.cardType || (profile.hasIdCard ? 'ID_CARD' : 'PASSPORT'),
      cardNo: profile.cardNo ?? '',
      birthdate: profile.birthdate ?? '',
      prefixName: profile.prefixName ?? '',
      firstName: profile.firstName ?? '',
      middleName: profile.middleName ?? '',
      lastName: profile.lastName ?? '',
      prefixNameEn: profile.prefixNameEn ?? '',
      firstNameEn: profile.firstNameEn ?? '',
      middleNameEn: profile.middleNameEn ?? '',
      lastNameEn: profile.lastNameEn ?? '',
      mobile: profile.mobile ?? '',
      email: profile.email ?? '',
      phoneNo: profile.phoneNo ?? '',
      faxNo: profile.faxNo ?? ''
    };
    this.profile.address = {
      addressNo: profile.addressNo ?? '',
      moo: profile.moo ?? '',
      soi: profile.soi ?? '',
      road: profile.road ?? '',
      location: profile.location ?? '',
      // ตำแหน่งปัจจุบัน is no longer asked for (work item 174); the stored coordinates
      // still round-trip so a profile save never wipes them.
      latitude: profile.latitude ?? null,
      longitude: profile.longitude ?? null,
      subDistrict: profile.subDistrictId ? {
        subdistrictId: profile.subDistrictId,
        text: profile.location ?? profile.subDistrictName ?? '',
        subdistrictTitle: profile.subDistrictName ?? '',
        districtTitle: profile.districtName ?? '',
        provinceTitle: profile.provinceName ?? '',
        districtId: profile.districtId ?? null,
        provinceId: profile.provinceId ?? null,
        zipcode: profile.postCode ?? ''
      } : null,
      subDistrictId: profile.subDistrictId ?? null,
      subdistrictName: profile.subDistrictName ?? '',
      districtId: profile.districtId ?? null,
      districtName: profile.districtName ?? '',
      provinceId: profile.provinceId ?? null,
      provinceName: profile.provinceName ?? '',
      postCode: profile.postCode ?? ''
    };

    return {summary, completed};
  }

  private buildSavePayload(): UserProfileDto {
    const pi = this.profile.personalInfo ?? {};
    const addr = this.profile.address ?? {};
    const selectedUserRoles: UserRole[] = ['USER'];
    return {
      id: this.currentUserSummary.id,
      username: this.currentUserSummary.username,
      email: pi.email,
      cardNo: pi.cardNo,
      fullName: [pi.prefixName, pi.firstName, pi.lastName].filter((item: string) => !!item).join(' ').trim(),
      status: Boolean(this.profile.status),
      userRoles: selectedUserRoles,
      hasIdCard: pi.cardType === 'ID_CARD',
      cardType: pi.cardType,
      birthdate: pi.birthdate,
      prefixName: pi.prefixName,
      firstName: pi.firstName,
      middleName: pi.middleName,
      lastName: pi.lastName,
      prefixNameEn: pi.prefixNameEn,
      firstNameEn: pi.firstNameEn,
      middleNameEn: pi.middleNameEn,
      lastNameEn: pi.lastNameEn,
      mobile: pi.mobile,
      phoneNo: pi.phoneNo,
      faxNo: pi.faxNo,
      addressNo: addr.addressNo,
      moo: addr.moo,
      soi: addr.soi,
      road: addr.road,
      location: addr.location,
      latitude: addr.latitude ?? null,
      longitude: addr.longitude ?? null,
      subDistrictId: addr.subDistrictId ?? null,
      subDistrictName: addr.subdistrictName,
      districtId: addr.districtId ?? null,
      districtName: addr.districtName,
      provinceId: addr.provinceId ?? null,
      provinceName: addr.provinceName,
      postCode: addr.postCode,
      profileCompleted: this.profileCompleted
    };
  }

  private scrollToFirstInvalid(): void {
    const el = this.host.nativeElement.querySelector('.ng-invalid') as HTMLElement | null;
    if (!el) {
      return;
    }
    el.scrollIntoView({behavior: 'smooth', block: 'center'});
    const focusable = (el.matches('input, select, textarea') ? el : el.querySelector('input, select, textarea')) as HTMLElement | null;
    focusable?.focus({preventScroll: true});
  }
}
